import React from "react"
import Grid from "@mui/material/Grid"
import Card from "@mui/material/Card"
import IconButton from "@mui/material/IconButton"
import Box from "@mui/material/Box"
import Avatar from "@mui/material/Avatar"
import { Add } from "@mui/icons-material"
import { FileText, FileImage } from "lucide-react"
import moment from "moment"

import MDInput from "components/MDInput"
import MDTypography from "components/MDTypography"
import MDSelect from "components/MDSelect"

const getFileColor = (type) => {
  switch (type) {
    case "pdf":
      return "#ef5350"
    case "doc":
      return "#7e57c2"
    case "excel":
      return "#4caf50"
    default:
      return "#42a5f5"
  }
}

const getFileType = (file) => {
  const contentType = (file?.contentType || "").toLowerCase()
  if (contentType.includes("pdf")) return "pdf"
  if (contentType.includes("image")) return "image"
  if (contentType.includes("word")) return "doc"
  if (contentType.includes("excel") || contentType.includes("spreadsheet")) {
    return "excel"
  }
  const name = (file?.fileName || "").toLowerCase()
  if (name.endsWith(".pdf")) return "pdf"
  if (name.match(/\.(png|jpg|jpeg|gif|webp)$/)) return "image"
  if (name.match(/\.(doc|docx)$/)) return "doc"
  if (name.match(/\.(xls|xlsx|csv)$/)) return "excel"
  return "file"
}

const formatDate = (value) => {
  if (!value) return "-"
  return moment(value).format("DD/MM/YYYY")
}

const FileItem = ({ file }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 1,
      px: 1.25,
      py: 1,
      border: "1px dashed #d6d6d6",
      borderRadius: "8px",
      backgroundColor: "#fff",
      minWidth: "140px",
      maxWidth: "180px",
    }}
  >
    <Box
      sx={{
        width: 18,
        height: 18,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: getFileColor(file.type),
        flexShrink: 0,
      }}
    >
      {file.type === "image" ? <FileImage size={16} /> : <FileText size={16} />}
    </Box>

    <MDTypography
      variant="caption"
      sx={{
        fontSize: "13px",
        color: "#344767",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {file.name}
    </MDTypography>
  </Box>
)

function LeaveRequestBasicInformation({ data }) {
  const fileList = (data?.attachments || []).map((file) => ({
    name: file?.fileName || "-",
    type: getFileType(file),
    url: file?.url,
  }))
  const teacherName = data?.employeeFullName || "-"
  const teacherAvatar = data?.employeeAvatar
  const leaveType = data?.leaveType || "-"
  const reason = data?.reason || "-"
  const startDate = formatDate(data?.startDate)
  const endDate = formatDate(data?.endDate)
  const avatarLetter = teacherName !== "-" ? teacherName[0] : "?"

  return (
    <Card
      sx={{
        borderRadius: "0",
        boxShadow: "none",
      }}
    >
      <MDTypography
        variant="h6"
        fontWeight="bold"
        sx={{ mb: 3, color: "#344767" }}
      >
        1. Basic Information
      </MDTypography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <MDTypography
            variant="button"
            fontWeight="bold"
            sx={{ display: "block", mb: 1 }}
          >
            Staff name
          </MDTypography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              minHeight: "42px",
            }}
          >
            <Avatar
              src={teacherAvatar || undefined}
              sx={{
                width: 24,
                height: 24,
                fontSize: "12px",
                bgcolor: "#1a73e8",
              }}
            >
              {avatarLetter}
            </Avatar>
            <MDTypography variant="button" fontWeight="medium">
              {teacherName}
            </MDTypography>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <MDTypography
            variant="button"
            fontWeight="bold"
            sx={{ display: "block", mb: 1 }}
          >
            Leave type
          </MDTypography>

          <MDSelect
            isDisabled
            value={{ value: leaveType, label: leaveType }}
            options={[{ value: leaveType, label: leaveType }]}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <MDTypography
            variant="button"
            fontWeight="bold"
            sx={{ display: "block", mb: 1 }}
          >
            Start date
          </MDTypography>

          <MDInput
            fullWidth
            value={startDate}
            InputProps={{ readOnly: true }}
            disabled
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <MDTypography
            variant="button"
            fontWeight="bold"
            sx={{ display: "block", mb: 1 }}
          >
            End date
          </MDTypography>

          <MDInput
            fullWidth
            value={endDate}
            InputProps={{ readOnly: true }}
            disabled
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <MDTypography
            variant="button"
            fontWeight="bold"
            sx={{ display: "block", mb: 1 }}
          >
            Reason
          </MDTypography>

          <MDInput
            fullWidth
            multiline
            rows={4}
            value={reason}
            InputProps={{ readOnly: true }}
            disabled
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <MDTypography
            variant="button"
            fontWeight="bold"
            sx={{ display: "block", mb: 1 }}
          >
            Attach file
          </MDTypography>

          <Box
            sx={{
              minHeight: "110px",
              pb: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1.25,
                alignItems: "center",
              }}
            >
              {fileList.length > 0 ? (
                fileList.map((file, index) => (
                  <FileItem key={index} file={file} />
                ))
              ) : (
                <MDTypography variant="caption">No attachments</MDTypography>
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Card>
  )
}

export default LeaveRequestBasicInformation
